import type { GetOptions, OrderByOptions, QueryConstraint } from "@/core";

/** Page size for one-shot "fetch everything" reads (exports, derived totals). */
export const FETCH_ALL_PAGE_SIZE = 500;

interface FetchAllPagesOptions<TEntity> {
  queryConstraints?: QueryConstraint<keyof TEntity & string>[];
  orderBy: OrderByOptions;
  pageSize?: number;
}

/**
 * Auto-pages a repository `getAll` until the collection is exhausted.
 *
 * Exports and derived financial totals use this rather than a single large
 * `limit`, which silently truncates once a collection outgrows it.
 */
export async function fetchAllPages<TEntity extends { id: string }>(
  getAll: (options: GetOptions<TEntity>) => Promise<TEntity[]>,
  options: FetchAllPagesOptions<TEntity>,
): Promise<TEntity[]> {
  const pageSize = options.pageSize ?? FETCH_ALL_PAGE_SIZE;
  const records: TEntity[] = [];
  let cursor = "";

  for (;;) {
    const page = await getAll({
      queryConstraints: options.queryConstraints,
      orderBy: options.orderBy,
      pagination: { limit: pageSize, cursor },
    });

    records.push(...page);

    if (page.length < pageSize) {
      return records;
    }

    cursor = page[page.length - 1].id;
  }
}
